import React, { useContext, useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Divider, Text, DropdownItem, TagColored, TagGeneral } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { usePlanTier } from '@/features/billing/hooks/usePlanTier';
import { FeedbackModal } from '@/features/feedback';
import CreateWorkspaceModal from '@/features/workspaces/components/CreateWorkspaceModal';
import WorkspaceSwitcher from '@/features/workspaces/components/WorkspaceSwitcher';
import '@/app/layout/HorizontalNavBar.css';

/** ``/settings`` plus the usecase id when one is active (same shape the old gear used). */
function settingsPath(usecaseId) {
    return usecaseId ? `/settings?usecaseId=${usecaseId}` : '/settings';
}

const WORKSPACE_ROLES = new Set(['Admin', 'Editor', 'Viewer']);

/**
 * Role tag for the active workspace (Figma 9073:1436): gods act as Admin
 * everywhere; members without a real role ("Unassigned", null) get no tag.
 */
export function roleTagLabel({ role, platformRole, isAuthEnabled }) {
    if (!isAuthEnabled || platformRole === 'god') return 'Admin';
    return WORKSPACE_ROLES.has(role) ? role : null;
}

export function UserMenu() {
    const navigate = useNavigate();
    const location = useLocation();
    const globalState = useContext(GlobalContext);
    const currentUsecaseId = globalState?.currentUsecaseId;
    const { user, logout, role, platformRole, isAuthEnabled, feedbackEnabled } = useAuth();
    const planTier = usePlanTier();
    const [open, setOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        function handleOutside(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [open]);

    const displayName = user?.name || user?.email || '';
    const avatarType = user?.picture ? 'image' : 'initials';
    const roleLabel = roleTagLabel({ role, platformRole, isAuthEnabled });

    function handleGiveFeedback() {
        setOpen(false);
        setFeedbackOpen(true);
    }

    function handleAddWorkspace() {
        setOpen(false);
        setCreateWorkspaceOpen(true);
    }

    function handleSettings() {
        setOpen(false);
        // Figma 9150:4071: Settings moved from the nav gear into this menu. Keep the
        // return location so the Settings back button lands where the user was.
        const returnTo = `${location.pathname}${location.search}${location.hash}`;
        navigate(settingsPath(currentUsecaseId), { state: { from: returnTo } });
    }

    async function handleLogOut() {
        setOpen(false);
        await logout();
        navigate('/login');
    }

    return (
        <div ref={wrapRef} className="home-nav-user-menu">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="home-nav-user-menu-trigger"
                aria-label="User menu"
                aria-expanded={open}
            >
                <Avatar
                    type={avatarType}
                    src={user?.picture || undefined}
                    primaryText={displayName}
                    size="small"
                />
            </button>

            {open && (
                <div className="home-nav-user-menu-panel">
                    <div className="spark-dropdown home-nav-user-menu-dropdown" role="menu">
                        <div className="home-nav-user-menu-identity">
                            <div className="home-nav-user-menu-identity-row">
                                <Text variant="lead-medium" as="p" className="home-nav-user-menu-name">
                                    {displayName}
                                </Text>
                                <div className="home-nav-user-menu-tags">
                                    {planTier === 'pro' ? (
                                        <TagGeneral label="Pro" endIconSlug="bolt" />
                                    ) : null}
                                    {planTier === 'free' ? <TagColored variant="disabled" label="Free" /> : null}
                                    {roleLabel ? <TagColored variant="purple" label={roleLabel} /> : null}
                                </div>
                            </div>
                            {user?.email ? (
                                <Text variant="detail-regular" color="secondary" as="p">
                                    {user.email}
                                </Text>
                            ) : null}
                        </div>
                        <Divider />
                        <WorkspaceSwitcher onAddWorkspace={handleAddWorkspace} />
                        <Divider />
                        {feedbackEnabled ? (
                            <DropdownItem
                                label="Give us Feedback"
                                showPrefixIcon
                                prefixIconSlug="megaphone"
                                onClick={handleGiveFeedback}
                            />
                        ) : null}
                        <DropdownItem
                            label="Settings"
                            showPrefixIcon
                            prefixIconSlug="settings"
                            onClick={handleSettings}
                        />
                        {isAuthEnabled ? (
                            <DropdownItem
                                label="Log Out"
                                showPrefixIcon
                                prefixIconSlug="arrow-right"
                                onClick={handleLogOut}
                            />
                        ) : null}
                    </div>
                </div>
            )}
            {feedbackEnabled ? (
                <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
            ) : null}
            <CreateWorkspaceModal open={createWorkspaceOpen} onClose={() => setCreateWorkspaceOpen(false)} />
        </div>
    );
}
